import { DragEndEvent } from '@dnd-kit/core';
import { computed, flow, makeObservable, observable } from 'mobx';
import moment, { Moment } from 'moment';
import { hasValue } from 'src/shared/utils/common';
import { createPromiseController, TPromiseController } from 'src/shared/utils/promise-controller';
import { Directories } from 'src/store/directories/directories.store';

import { TOption } from '../../types';
import { FormStore } from '../form.entity';

import { RigOperationSection } from './rig-operation-section.entity';
import { RigOperationStage } from './rig-operation-stage.entity';
import { RigOperation } from './rig-operation.entity';
import { isRigOperationField, isSectionField, isStageField, RigOperationsService } from './rig-operations-service';
import { RigOperationsValidation } from './rig-operations-validation';

const defaultTime = moment().startOf('date');

export class RigOperationsViewModel {
  private directories: Directories;
  private formStore: FormStore;
  readonly rigOperationsService: RigOperationsService;
  @observable private restoreRigOperationToDirectoryValuesPromiseController: TPromiseController<boolean> | null = null;

  constructor(formStore: FormStore, directories: Directories) {
    this.formStore = formStore;
    this.rigOperationsService = formStore.rigOperationsService;
    this.directories = directories;

    makeObservable(this);
  }

  @computed
  get restoreToDirectoryValueLoadingState(): Record<string, boolean> {
    const directoriesLoadingState = this.directories.directoriesStatuses;

    return {
      duration:
        directoriesLoadingState['Common_CommercialSpeed'] === 'loading' ||
        directoriesLoadingState['Common_WellPassing'] === 'loading',
      commercialSpeed: directoriesLoadingState['Common_CommercialSpeed'] === 'loading',
      rigOperation:
        directoriesLoadingState['Common_CommercialSpeed'] === 'loading' ||
        directoriesLoadingState['Common_WellPassing'] === 'loading',
    };
  }

  @computed
  get isDeleteButtonShown(): boolean {
    return !this.isFormDisabled;
  }

  @computed
  get isFormDisabled(): boolean {
    return this.formStore.isReadOnlyMode;
  }

  getValidSelectValue = (
    fieldId: 'rigId' | 'brigadeId' | 'wellLifeCycleStageId' | 'sectionTypeId',
    value: number | null,
  ): number | TOption | null => {
    if (!hasValue(value)) {
      return null;
    }

    switch (fieldId) {
      case 'rigId': {
        return this.rigOptions.archived.find((opt) => opt.value === value) ?? value ?? null;
      }
      case 'brigadeId': {
        return this.brigadeOptions.archived.find((opt) => opt.value === value) ?? value ?? null;
      }
      case 'wellLifeCycleStageId': {
        return this.stageTypeOptions.archived.find((opt) => opt.value === value) ?? value ?? null;
      }
      case 'sectionTypeId': {
        return this.sectionTypeOptions.archived.find((opt) => opt.value === value) ?? value ?? null;
      }
    }
  };

  @computed
  get rigOptions(): { actual: TOption<number>[]; archived: TOption<number>[] } {
    const rigsDirectory = this.directories.getObject('Common_Rig');
    const rigModelsDirectory = this.directories.getObject('Common_Rig_Model');
    const organizationsDirectory = this.directories.getObject('Common_Organization');

    if (!rigsDirectory) {
      console.error('Common_Rig directory is not presented');
      return { actual: [], archived: [] };
    }

    return rigsDirectory.reduce(
      (acc: { actual: TOption<number>[]; archived: TOption<number>[] }, dv) => {
        const isArchived = dv.status === 'DELETED';

        const rigNameOrModel =
          (
            dv.data['name '] || rigModelsDirectory?.find((d) => d.id === dv.data['modelId'])?.data['name']
          )?.toString() ?? 'Unnamed';

        const rigNumber = dv.data['rigNumber'] ?? 'No rig number';
        const organizationName =
          organizationsDirectory?.find((d) => d.id === dv.data['drillingContractorId'])?.data['name'] ??
          // TODO: localization
          'No organization';

        const option = {
          label: `${rigNameOrModel} / ${rigNumber} / ${organizationName}`,
          value: dv.id as number,
        };

        if (isArchived) {
          acc.archived.push(option);
        } else {
          acc.actual.push(option);
        }

        return acc;
      },
      { archived: [], actual: [] },
    );
  }

  @computed
  get brigadeOptions(): { actual: TOption<number>[]; archived: TOption<number>[] } {
    const directory = this.directories.getObject('Common_Brigade');

    if (!directory) {
      console.error('Common_Brigade directory is not presented');
      return { actual: [], archived: [] };
    }

    return directory.reduce(
      (acc: { actual: TOption<number>[]; archived: TOption<number>[] }, dv) => {
        const isArchived = dv.status === 'DELETED';
        const label = dv.data['name']?.toString() ?? 'No name';

        const option = {
          label,
          value: dv.id as number,
        };

        if (isArchived) {
          acc.archived.push(option);
        } else {
          acc.actual.push(option);
        }

        return acc;
      },
      { archived: [], actual: [] },
    );
  }

  @computed
  get stageTypeOptions(): { actual: TOption<number>[]; archived: TOption<number>[] } {
    const directory = this.directories.getObject('GOplan_Dict_WellLifeCycleStage');

    if (!directory) {
      console.error('GOplan_Dict_WellLifeCycleStage directory is not presented');
      return { actual: [], archived: [] };
    }

    return directory.reduce(
      (acc: { actual: TOption<number>[]; archived: TOption<number>[] }, dv) => {
        const isArchived = dv.status === 'DELETED';
        const label = dv.data['name']?.toString() ?? 'No name';

        const option = {
          label,
          value: dv.id as number,
        };

        if (isArchived) {
          acc.archived.push(option);
        } else {
          acc.actual.push(option);
        }

        return acc;
      },
      { archived: [], actual: [] },
    );
  }

  @computed
  get sectionTypeOptions(): { actual: TOption<number>[]; archived: TOption<number>[] } {
    const directory = this.directories.getObject('Common_SectionType');

    if (!directory) {
      console.error('Common_SectionType directory is not presented');
      return { actual: [], archived: [] };
    }

    return directory.reduce(
      (acc: { actual: TOption<number>[]; archived: TOption<number>[] }, dv) => {
        const isArchived = dv.status === 'DELETED';
        const label = dv.data['name']?.toString() ?? 'No name';

        const option = {
          label,
          value: dv.id as number,
        };

        if (isArchived) {
          acc.archived.push(option);
        } else {
          acc.actual.push(option);
        }

        return acc;
      },
      { archived: [], actual: [] },
    );
  }

  get validationService(): Omit<RigOperationsValidation, 'validateRigOperation' | 'validateStage' | 'validateSection'> {
    return this.rigOperationsService.validationService;
  }

  private getPrevDate = (
    fieldId: 'startDate' | 'endDate',
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ): Moment | null => {
    const findNodeWithEndDateInGivenStages = (stages: RigOperationStage[]) => {
      for (const stage of stages) {
        if (!stage.endDate && !stage.sections.length) {
          continue;
        }

        if (stage.sections.length) {
          const reversedSections = [...stage.sections].reverse();
          const section = reversedSections.find((sec) => sec.endDate);

          if (section) {
            return section;
          }

          continue;
        }

        if (stage.endDate) {
          return stage;
        }
      }

      return null;
    };

    const prevNodeWithEndDate = ((): RigOperationStage | RigOperationSection | null => {
      if (section) {
        const sectionIndex = stage.sections.indexOf(section);

        if (sectionIndex > 0) {
          const copiedReversedSections = [...stage.sections.slice(0, sectionIndex)].reverse();

          const node = copiedReversedSections.find((sec) => sec.endDate) ?? null;

          if (node) {
            return node;
          }
        }
      }

      const stageIndex = rigOperation.stages.indexOf(stage);

      if (stageIndex > 0) {
        const prevReversedStages = [...rigOperation.stages.slice(0, stageIndex)].reverse();

        const node = findNodeWithEndDateInGivenStages(prevReversedStages);

        if (node) {
          return node;
        }
      }

      const rigOperationIndex = this.rigOperationsService.rigOperations.indexOf(rigOperation);

      if (rigOperationIndex < 1) {
        return null;
      }

      const prevReversedRigOperations = [
        ...this.rigOperationsService.rigOperations.slice(0, rigOperationIndex),
      ].reverse();

      for (const rigOp of prevReversedRigOperations) {
        if (!rigOp.stages.length) {
          continue;
        }

        const reversedStages = [...rigOp.stages].reverse();

        const node = findNodeWithEndDateInGivenStages(reversedStages);

        if (node) {
          return node;
        }
      }

      return null;
    })();

    switch (fieldId) {
      case 'startDate': {
        return prevNodeWithEndDate?.endDate ? moment(prevNodeWithEndDate.endDate * 1000) : null;
      }
      case 'endDate': {
        if (section) {
          const startDate = section.startDate;

          if (startDate) {
            return moment(startDate * 1000);
          }

          return prevNodeWithEndDate?.endDate ? moment(prevNodeWithEndDate.endDate * 1000) : null;
        }

        const stageStartDate = stage.startDate;

        if (stageStartDate) {
          return moment(stageStartDate * 1000);
        }

        return prevNodeWithEndDate?.endDate ? moment(prevNodeWithEndDate.endDate * 1000) : null;
      }
    }
  };

  setDirectoryValue = (
    fieldId: string,
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ) => this.rigOperationsService.setDirectoryValue(fieldId, rigOperation, stage, section);

  onValueChange = (
    fieldId: string,
    value: unknown,
    rigOperation: RigOperation,
    stage?: RigOperationStage,
    section?: RigOperationSection,
  ): void => {
    const parsedValue = (() => {
      if (['startDate', 'endDate'].includes(fieldId) && moment.isMoment(value)) {
        return value.unix();
      }

      return value;
    })();

    this.rigOperationsService.onValueChange(fieldId, parsedValue, { rigOperation, stage, section });
  };

  isRigOperationFieldBlocked = (fieldId: string, rigOperation: RigOperation): boolean => {
    if (this.isFormDisabled) {
      return true;
    }

    if (!isRigOperationField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return false;
    }

    return false;
  };

  isStageFieldBlocked = (fieldId: string, rigOperation: RigOperation, stage: RigOperationStage): boolean => {
    if (this.isFormDisabled) {
      return true;
    }

    if (!isStageField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return false;
    }

    if (stage.sections.length && fieldId !== 'wellLifeCycleStageId') {
      return true;
    }

    switch (fieldId) {
      case 'startDate': {
        const stageIndex = rigOperation.stages.indexOf(stage);

        if (stageIndex > 0) {
          return true;
        }

        break;
      }
      case 'endDate': {
        if (!stage.startDate) {
          return true;
        }

        break;
      }
    }

    return false;
  };

  isSectionFieldBlocked = (
    fieldId: string,
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section: RigOperationSection,
  ): boolean => {
    if (this.isFormDisabled) {
      return true;
    }

    if (!isSectionField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return false;
    }

    switch (fieldId) {
      case 'startDate': {
        const stageIndex = rigOperation.stages.indexOf(stage);
        const sectionIndex = stage.sections.indexOf(section);

        if (stageIndex === 0 && sectionIndex === 0) {
          return false;
        }

        return true;
      }
      case 'endDate': {
        if (!section.startDate) {
          return true;
        }

        break;
      }
    }

    return false;
  };

  isStageFieldVisible = (fieldId: string, rigOperation: RigOperation, stage: RigOperationStage): boolean => {
    if (!isStageField(fieldId)) {
      console.error('invalid fieldId', fieldId);
      return false;
    }

    switch (fieldId) {
      case 'commercialSpeed': {
        if (this.rigOperationsService.rigOperations.length < 2 || !this.isDrillingStage(stage.wellLifeCycleStageId)) {
          return false;
        }

        return true;
      }
      case 'passingValue': {
        if (
          (this.rigOperationsService.rigOperations.length < 2 && !stage.sections.length) ||
          !this.isDrillingStage(stage.wellLifeCycleStageId)
        ) {
          return false;
        }

        return true;
      }
      case 'distance': {
        if (this.hasDistance(stage.wellLifeCycleStageId)) {
          return true;
        }

        return false;
      }
    }

    return true;
  };

  @computed
  get isLoseChangesModalOpened(): boolean {
    return !!this.restoreRigOperationToDirectoryValuesPromiseController;
  }

  approveRigOperationResetToDirectoryValues = () => {
    this.restoreRigOperationToDirectoryValuesPromiseController?.resolve(true);
  };
  cancelRigOperationResetToDirectoryValues = () => {
    this.restoreRigOperationToDirectoryValuesPromiseController?.resolve(false);
  };

  @flow.bound
  async *restoreRigOperationToDirectoryValues(rigOperation: RigOperation) {
    this.restoreRigOperationToDirectoryValuesPromiseController = createPromiseController();

    const isApproved = await this.restoreRigOperationToDirectoryValuesPromiseController;
    yield;

    if (isApproved) {
      this.rigOperationsService.restoreRigOperationToDirectoryValues(rigOperation);
    }

    this.restoreRigOperationToDirectoryValuesPromiseController = null;
  }

  getDefaultTime = (
    fieldId: 'startDate' | 'endDate',
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ): Moment => {
    const currentValue = (() => {
      if (section) {
        return section[fieldId] ? moment(section[fieldId]! * 1000) : null;
      }

      return stage[fieldId] ? moment(stage[fieldId]! * 1000) : null;
    })();

    return currentValue ?? this.getPrevDate(fieldId, rigOperation, stage, section) ?? defaultTime;
  };

  getDisabledTime = (
    fieldId: 'startDate' | 'endDate',
    rigOperation: RigOperation,
    stage: RigOperationStage,
    section?: RigOperationSection,
  ): Moment | null => {
    return this.getPrevDate(fieldId, rigOperation, stage, section);
  };

  moveStage = (rigOperation: RigOperation, event: DragEndEvent): void => {
    const overStageId = event.over?.id;
    const activeStageId = event.active.id;

    if (!overStageId) {
      return;
    }

    const overStageIndex = rigOperation.stages.findIndex((s) => s.entityId === overStageId);
    const activeStageIndex = rigOperation.stages.findIndex((s) => s.entityId === activeStageId);

    if (overStageIndex < 0 || activeStageIndex < 0) {
      return;
    }

    this.rigOperationsService.moveStage(rigOperation, activeStageIndex, overStageIndex);
  };

  moveSection = (rigOperation: RigOperation, stage: RigOperationStage, event: DragEndEvent): void => {
    const overSectionId = event.over?.id;
    const activeSectionId = event.active.id;

    if (!overSectionId) {
      return;
    }

    const overSectionIndex = stage.sections.findIndex((s) => s.entityId === overSectionId);
    const activeSectionIndex = stage.sections.findIndex((s) => s.entityId === activeSectionId);

    if (overSectionIndex < 0 || activeSectionIndex < 0) {
      return;
    }

    this.rigOperationsService.moveSection(rigOperation, stage, activeSectionIndex, overSectionIndex);
  };

  isDrillingStage = (stageTypeId: number | null): boolean => this.rigOperationsService.isDrillingStage(stageTypeId);
  hasSections = (stageTypeId: number | null): boolean => this.rigOperationsService.hasSections(stageTypeId);
  hasDistance = (stageTypeId: number | null): boolean => this.rigOperationsService.hasDistance(stageTypeId);

  deleteRigOperation = (rigOperation: RigOperation): void => this.rigOperationsService.deleteRigOperation(rigOperation);
  deleteStage = (rigOperation: RigOperation, stage: RigOperationStage): void =>
    this.rigOperationsService.deleteStage(rigOperation, stage);
  deleteSection = (rigOperation: RigOperation, stage: RigOperationStage, section: RigOperationSection): void =>
    this.rigOperationsService.deleteSection(rigOperation, stage, section);

  addRigOperation = (): void => this.rigOperationsService.addRigOperation();
  addStage = (rigOperation: RigOperation, stageTypeId: number): void =>
    this.rigOperationsService.addStage(rigOperation, { wellLifeCycleStageId: stageTypeId });
  addSection = (rigOperation: RigOperation, stage: RigOperationStage, sectionTypeId: number): void =>
    this.rigOperationsService.addSection(rigOperation, stage, { sectionTypeId });
}
